/**
 * Утилиты для работы с временем и датами
 */

/**
 * Создает дату в локальном часовом поясе из ISO строки
 * @param isoString - ISO строка времени
 * @returns Date объект в локальном часовом поясе
 */
const createLocalDateFromISO = (isoString: string): Date => {
  // Если строка заканчивается на 'Z', это UTC время
  if (isoString.endsWith('Z')) {
    return new Date(isoString);
  }

  // Если строка содержит информацию о часовом поясе (например, +03:00)
  if (isoString.includes('+') || (isoString.includes('-') && isoString.lastIndexOf('-') > 10)) {
    return new Date(isoString);
  }

  // Если это просто дата и время без информации о часовом поясе,
  // считаем что это UTC время (старые данные) и конвертируем в локальное
  const parts = isoString.split('T');
  if (parts.length === 2) {
    // Добавляем 'Z' чтобы указать что это UTC время
    const utcString = isoString + 'Z';
    return new Date(utcString);
  }

  // Fallback к стандартному парсингу
  return new Date(isoString);
};

/**
 * Форматирует время для отображения в списке чатов
 * Показывает дату, если день не сегодняшний, иначе только время
 * @param timeString - строка времени в формате ISO или HH:MM
 * @returns отформатированная строка времени/даты
 */
export const formatChatTime = (timeString: string): string => {
  try {
    let date: Date;

    // Если время в формате HH:MM, создаем дату на сегодня
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      const today = new Date();
      const [hours, minutes] = timeString.split(':');
      date = new Date(today.getFullYear(), today.getMonth(), today.getDate(),
        parseInt(hours), parseInt(minutes));
    } else {
      // Пытаемся парсить как ISO строку
      date = createLocalDateFromISO(timeString);
    }

    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) {
      return timeString; // Возвращаем исходную строку, если не удалось распарсить
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Если сообщение сегодняшнее, показываем только время
    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Moscow'
      });
    }

    // Если сообщение вчерашнее
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.getTime() === yesterday.getTime()) {
      return 'Вчера';
    }

    // Если сообщение старше, показываем дату
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      timeZone: 'Europe/Moscow'
    });
  } catch (error) {
    console.error('Ошибка форматирования времени:', error);
    return timeString;
  }
};

/**
 * Форматирует время для отображения в сообщениях чата
 * @param timeString - строка времени в формате ISO или HH:MM
 * @returns отформатированная строка времени
 */
export const formatMessageTime = (timeString: string): string => {
  try {
    let date: Date;

    // Если время в формате HH:MM, создаем дату на сегодня
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      const today = new Date();
      const [hours, minutes] = timeString.split(':');
      date = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        parseInt(hours, 10),
        parseInt(minutes, 10)
      );
    } else {
      date = createLocalDateFromISO(timeString);
    }

    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) {
      return timeString;
    }

    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow'
    });
  } catch (error) {
    console.error('Ошибка форматирования времени сообщения:', error);
    return timeString;
  }
};

/**
 * Создает текущее время в локальном часовом поясе в формате HH:MM
 * @returns строка времени в формате HH:MM
 */
export const getCurrentTimeString = (): string => {
  const now = new Date();
  return now.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Создает текущую дату и время в ISO формате с учетом локального часового пояса
 * @returns строка даты и времени в ISO формате
 */
export const getCurrentISOString = (): string => {
  // Возвращаем корректное ISO UTC время (c 'Z'), чтобы избежать двойного учета часового пояса
  return new Date().toISOString();
};